import { useEffect } from 'react';
import { useUser, useAuth } from '@clerk/clerk-react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/store/auth-store';
import { apiClient } from '@/services/api-client';
import type { User } from '@/types';
import { Loader2, WifiOff, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface AuthGuardProps {
  children: React.ReactNode;
}

export function AuthGuard({ children }: AuthGuardProps) {
  const { isLoaded, isSignedIn, user: clerkUser } = useUser();
  const { getToken } = useAuth();
  const location = useLocation();

  const user = useAuthStore((state) => state.user);
  const isLoading = useAuthStore((state) => state.isLoading);
  const error = useAuthStore((state) => state.error);
  const setUser = useAuthStore((state) => state.setUser);
  const setLoading = useAuthStore((state) => state.setLoading);
  const setError = useAuthStore((state) => state.setError);

  useEffect(() => {
    async function fetchUser() {
      if (!isLoaded || !isSignedIn || !clerkUser) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        apiClient.setTokenGetter(getToken);

        const response = await apiClient.get<User>('/api/v1/users/me');

        if (response.success && response.data) {
          setUser(response.data);
        } else {
          setError(response.error?.message || 'Failed to fetch user');
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    }

    fetchUser();
  }, [isLoaded, isSignedIn, clerkUser, getToken, setUser, setLoading, setError]);

  if (!isLoaded || isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isSignedIn) {
    return <Navigate to="/sign-in" state={{ from: location }} replace />;
  }

  if (error) {
    const isNetworkError =
      error.includes('Failed to fetch') ||
      error.includes('Network') ||
      error.includes('NETWORK_ERROR') ||
      error.includes('ERR_CONNECTION');

    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4 text-center max-w-md px-4">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
            {isNetworkError ? (
              <WifiOff className="h-8 w-8 text-destructive" />
            ) : (
              <AlertTriangle className="h-8 w-8 text-destructive" />
            )}
          </div>
          <h2 className="text-xl font-semibold">
            {isNetworkError ? 'Unable to Connect' : 'Something Went Wrong'}
          </h2>
          <p className="text-muted-foreground">
            {isNetworkError
              ? 'Our backend system may be temporarily unavailable. Please try again later or contact your administrator if the issue persists.'
              : error}
          </p>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => {
                setError(null);
                setLoading(true);
                window.location.reload();
              }}
            >
              Try Again
            </Button>
            <Button variant="outline" onClick={() => (window.location.href = '/sign-in')}>
              Back to Sign In
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Loading user data...</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
